import {useCallback, useState} from 'react';
import {t, tt} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {TextTKey} from '../../../common/type';
import {KakaoentPolicyType} from '../../../domain/policy/type';
import {getKakaoentPolicyUrl} from '../../../domain/policy/util/getKakaoentPolicyUrl';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {WithSeparator} from '../../molecules/WithSeparator/WithSeparator';
import {FooterMenuItem} from './FooterMenuItem';
import {FooterProps} from './FooterProps.interface';

export function Footer({
  isMobile,
  shouldShowFooterLinkMenu = true,
  className,
  ...props
}: FooterProps) {
  const [isShowMobileBusinessInfo, setIsShowMobileBusinessInfo] =
    useState(false);

  const onClickBusinessInfoTitle = useCallback(() => {
    setIsShowMobileBusinessInfo(prev => !prev);
  }, []);
  return (
    <footer
      className={twMerge(
        'flex bg-theme-bg-base',
        isMobile
          ? 'border-t-1 border-t-theme-transparent-20 py-35pxr px-15pxr'
          : 'mx-auto w-full min-w-1200pxr flex-col items-center pb-42pxr',
        className
      )}
      {...props}
    >
      {/* 실제 Footer를 구성하는 영역 */}
      <div
        className={twMerge(
          'flex w-956pxr flex-1 justify-center',
          !isMobile && 'pl-15pxr'
        )}
      >
        {/* 텍스트 영역 */}
        <div className="mb-20pxr flex flex-1 flex-col">
          <div className={twMerge(!isMobile && 'space-x-16pxr')}>
            {isMobile && shouldShowFooterLinkMenu && (
              <>
                <div className="flex items-center">
                  <WithSeparator separatorClassName="bg-theme-transparent-20 h-10pxr mx-10pxr">
                    {FOOTER_MENU_LIST.slice(0, 3).map(menu => (
                      <FooterMenuItem
                        isMobile={isMobile}
                        useKakaoenterPage={!!menu.useKakaoenterPage}
                        href={menu.href}
                        emphasis={menu.emphasis}
                        title={tt(menu)}
                        key={menu.text}
                        scheme={menu.scheme}
                      />
                    ))}
                  </WithSeparator>
                </div>
                <div className="mt-9pxr mb-20pxr flex items-center">
                  <WithSeparator separatorClassName="bg-theme-transparent-20 h-10pxr mx-10pxr">
                    {FOOTER_MENU_LIST.slice(3, 6).map(menu => (
                      <FooterMenuItem
                        isMobile={isMobile}
                        useKakaoenterPage={!!menu.useKakaoenterPage}
                        href={menu.href}
                        emphasis={menu.emphasis}
                        title={tt(menu)}
                        key={menu.text}
                        scheme={menu.scheme}
                      />
                    ))}
                  </WithSeparator>
                </div>
              </>
            )}
            {!isMobile &&
              shouldShowFooterLinkMenu &&
              FOOTER_MENU_LIST.map(menu => (
                <FooterMenuItem
                  isMobile={isMobile}
                  useKakaoenterPage={!!menu.useKakaoenterPage}
                  href={menu.href}
                  emphasis={menu.emphasis}
                  title={tt(menu)}
                  key={menu.text}
                  scheme={menu.scheme}
                />
              ))}
          </div>
          {/* Mobile만 존재하는 아코디언 타이틀 */}
          {isMobile && (
            <div
              className="flex items-center"
              onClick={onClickBusinessInfoTitle}
            >
              <Text className="font-small2 text-theme-solid-40">
                {t('common_footer_bussiness_info')}
              </Text>
              <Icon
                className="ml-4pxr"
                type={
                  isShowMobileBusinessInfo
                    ? IconType.TabArrowUp
                    : IconType.TabArrowDown
                }
              />
            </div>
          )}
          {/* Mobile 사업자정보 내용 (모바일인 경우에 아코디언 열려있을 때에만 노출) */}
          {isMobile && isShowMobileBusinessInfo && (
            <div className="font-small2 mt-5pxr mb-15pxr flex flex-1 flex-col justify-center text-theme-solid-40">
              <Text>{t('common_footer_bussiness_representive')}</Text>
              <div className="space-x-6pxr">
                <Text>{t('common_footer_bussiness_host')}</Text>
              </div>

              <Text>{t('common_footer_bussiness_registeration_number')}</Text>
              <Text>{t('common_footer_bussiness_mail_order')}</Text>
              <div className="space-x-6pxr">
                <Text>{t('common_footer_phone_number')}</Text>
                <Text>|</Text>
                <Text>{t('common_footer_email')}</Text>
              </div>
              <Text className="space-x-6pxr">
                <Text>{t('common_footer_address')}</Text>
                <Text>|</Text>
                <a
                  href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=2208802594"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {t('common_footer_bussiness_info_confirm')}
                </a>
              </Text>
            </div>
          )}
          {/* PC 사업자정보 내용 (항시노출) */}
          {!isMobile && (
            <div className="font-small1 mt-30pxr flex flex-col justify-center text-theme-solid-40">
              <div>{t('common_footer_bussiness_info')}</div>
              <div className="space-x-6pxr">
                <Text>{t('common_footer_bussiness_representive')}</Text>
                <Text>|</Text>
                <div className="inline-block space-x-6pxr">
                  <Text>{t('common_footer_bussiness_host')}</Text>
                </div>
              </div>
              <div className="space-x-6pxr">
                <Text>{t('common_footer_bussiness_registeration_number')}</Text>
                <Text>|</Text>
                <Text>{t('common_footer_bussiness_mail_order')}</Text>
              </div>
              <div className="space-x-6pxr">
                <Text>{t('common_footer_phone_number')}</Text>
                <Text>|</Text>
                <Text>{t('common_footer_email')}</Text>
                <Text>|</Text>
                <Text>{t('common_footer_address')}</Text>
                <Text>|</Text>
                <a
                  href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=2208802594"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer underline"
                >
                  {t('common_footer_bussiness_info_confirm')}
                </a>
              </div>
            </div>
          )}
          <div className={twMerge(!isMobile && 'mt-20pxr')}>
            <Text
              className={twMerge(
                isMobile ? 'font-small2' : 'font-small1',
                'text-theme-solid-40'
              )}
            >
              {t('common_footer_copyright')}
            </Text>
          </div>
        </div>
      </div>
    </footer>
  );
}

const FOOTER_MENU_LIST: (TextTKey & {
  href: string;
  emphasis: boolean;
  useKakaoenterPage?: boolean;
  scheme?: string;
})[] = [
  {
    text: '회사소개',
    textTKey: 'common_footer_company_introduce',
    href: 'https://www.kakaoent.com/',
    emphasis: false,
  },
  {
    text: '이용약관',
    textTKey: 'common_footer_term_use',
    href: '/policy/terms',
    emphasis: false,
  },
  {
    text: '개인정보처리방침',
    textTKey: 'common_footer_term_privacy',
    href: getKakaoentPolicyUrl({
      type: KakaoentPolicyType.Policy,
    }).urlExcludeVod().href,
    scheme: getKakaoentPolicyUrl({
      type: KakaoentPolicyType.Policy,
    }).webInAppScheme(),
    emphasis: true,
    useKakaoenterPage: true,
  },
  {
    text: '청소년보호정책',
    textTKey: 'common_footer_term_youth',
    href: '/policy/safeguard',
    emphasis: false,
  },
  {
    text: '고객센터',
    textTKey: 'common_footer_company_cs',
    href: '/support',
    emphasis: false,
  },
  {
    text: '공지사항',
    textTKey: 'common_footer_notification',
    href: '/notice',
    emphasis: false,
  },
];
